import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Stage, ChapterTag } from "@/components/Reveal";
import { Arch, Traveler, Thread, PulseGlow } from "@/components/Motifs";

const KEEPS = [
  { id: "becoming", label: "KEPT OPENING · 01", text: "“What are you in the middle of becoming…”" },
  { id: "beginning", label: "KEPT OPENING · 02", text: "A private beginning, held for you." },
  { id: "tuesday", label: "KEPT OPENING · 03", text: "Tuesday — the far place stayed open.", pulse: true },
];

const REMAINS_RANGES = {
  mattered: [0.06, 0.14, 0.26, 0.34],
  thread: [0.36, 0.44, 0.52, 0.6],
  statement: [0.78, 0.88],
};

const Niche = ({ k, i, progress }) => {
  const a = 0.5 + i * 0.05;
  const opacity = useTransform(progress, [a, a + 0.08], [0, 1]);
  const y = useTransform(progress, [a, a + 0.08], [46, 0]);
  const rx = useTransform(progress, [a, a + 0.1], [10, 0]);
  const gOp = useTransform(progress, [0.56, 0.66], [0, 1]);
  return (
    <motion.div
      className="th-niche"
      style={{ opacity, y, rotateX: rx, transformPerspective: 900 }}
      data-testid={`archive-niche-${i}`}
    >
      <p className="th-mini-label">{k.label}</p>
      <p className="th-niche-text">{k.text}</p>
      {i === 1 && <motion.span className="th-niche-glow" style={{ opacity: gOp }} />}
      {k.pulse && <PulseGlow className="absolute top-4 right-4" size={7} />}
    </motion.div>
  );
};

export default function Remains() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const thread = useTransform(p, [0.06, 0.68], [0, 1]);

  return (
    <section id="scene-remains" ref={ref} className="th-scene" style={{ height: "220vh" }} data-testid="scene-remains">
      <div className="th-sticky">
        <ChapterTag index="06" name="What Remains" />
        <div
          className="th-ghostword"
          style={{ top: "28%", left: "50%", transform: "translateX(-50%)", fontSize: "18vw" }}
          aria-hidden="true"
        >
          remains
        </div>
        <div className="absolute top-[9%] left-1/2 -translate-x-1/2 flex flex-col items-center">
          <Arch className="w-[110px] opacity-70" stroke="var(--ink-soft)" sw={1.2} />
          <Traveler className="h-10 -mt-3 opacity-80" data-testid="remains-traveler" />
        </div>
        <Thread
          d="M 500 -20 C 492 120, 508 260, 500 420 C 492 560, 508 720, 500 1010"
          viewBox="0 0 1000 1000"
          className="absolute inset-0 w-full h-full"
          progress={thread}
          stroke="var(--amber)"
          sw={1.3}
        />
        <Stage progress={p} range={REMAINS_RANGES.mattered}>
          <h3 className="th-statement th-halo">Keep what mattered.</h3>
        </Stage>
        <Stage progress={p} range={REMAINS_RANGES.thread}>
          <h3 className="th-question th-halo">Some conversations leave a thread.</h3>
        </Stage>
        <Stage progress={p} range={REMAINS_RANGES.statement} hold>
          <h3 className="th-display th-halo" data-testid="remains-statement">
            What remains.
          </h3>
        </Stage>
        <div className="absolute bottom-[6%] inset-x-0 flex flex-wrap justify-center gap-4 md:gap-6 px-6">
          {KEEPS.map((k, i) => (
            <Niche key={k.id} k={k} i={i} progress={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
